import { useState, useEffect, useRef } from 'react';
import { useCompanyId } from '../../../../core';
import { 
  getWhatIfScenario, 
  listWhatIfScenarios, 
  runWhatIfScenarioDirectlyForQAT, 
  updateWhatIfScenario 
} from '../../whatIfScenarios.client';
import { getAmProjects } from '../../../projects/amProject.client';
import { getAmProjectConfig } from '../../../../ps-models';
import { buildLineItemsStorePersistence } from '../../../../lineitems-store/RestLineItemsStore.persistence';
import { LineItemsStore } from '../../../../ps-models/lineitems-store';
import { 
  AmVersion, 
  getStoreId, 
  isFinalStateOfScenario,
  PFAScenarioConfiguration,
  ProjectPricingScenarioConfiguration,
  WhatIfScenarioRecord,
  WhatIfScenarioInputValues,
  WhatIfScenarioInput
} from '../../../../ps-types';
import { QATConversationObjectType } from '../../../../ps-types/QATAIConversation';
import { v4 as uuidv4 } from 'uuid';
import { authStorage } from '../../../../auth';
import { useMessages } from '../../../../ui/MessagesProvider';
import { PSCard } from '../../../../ui/PSCard';
import { Icon } from 'semantic-ui-react';
import { PFAResultBlock, ProjectPricingResultBlock } from '../QuickAnalysisToolsResultComponents';

const persistence = buildLineItemsStorePersistence();

/**
 * Maps conversation inputs to WhatIfScenarioInputValues format
 * @param config - The scenario configuration (PFAScenarioConfiguration or ProjectPricingScenarioConfiguration)
 * @param inputs - The conversation inputs object
 * @returns WhatIfScenarioInputValues object ready for scenario execution
 */
function mapInputsToScenarioFormat(
  config: any, 
  inputs: Record<string, number | string | null>
): WhatIfScenarioInputValues {
  const globalInputs: WhatIfScenarioInputValues = {};
  
  // Iterate over the mappingConfig to get label and valueKey pairs
  Object.entries(config.mappingConfig).forEach(([configKey, configValue]: [string, any]) => {
    const label = configValue.label;
    const valueKey = configValue.valueKey;
    
    // Find the matching input value by label
    const inputValue = inputs[label];
    
    // Only include inputs that have values
    if (inputValue !== null && inputValue !== undefined) {
      const scenarioInput: WhatIfScenarioInput = {
        valueKey: valueKey,
        value: [[inputValue]]
      };
      
      globalInputs[valueKey] = scenarioInput;
    }
  });
  
  return globalInputs;
}

interface ScenarioRunnerProps {
  kind: 'PFA' | 'Project-Pricing';
  inputs: QATConversationObjectType['inputs'];
  onComplete?: () => void;
}

export function ScenarioRunner({ kind, inputs, onComplete }: ScenarioRunnerProps) {
  const [scenario, setScenario] = useState<WhatIfScenarioRecord>();
  const [scenarioStatus, setScenarioStatus] = useState<WhatIfScenarioRecord['status']>();
  const [loading, setLoading] = useState<boolean>(true);
  const [running, setRunning] = useState<boolean>(false);
  const [versionData, setVersionData] = useState<AmVersion>();
  const [resultantStore, setResultantStore] = useState<LineItemsStore | undefined>(undefined);
  const [QATProjectId, setQATProjectId] = useState<string>();
  const { error: errorMessage, clear } = useMessages();
  const resultBlockRef = useRef<HTMLDivElement>(null);
  const hasInitializedRef = useRef<boolean>(false);

  const companyId = useCompanyId();
  const company = authStorage.getCompany();
  const { collection } = getAmProjectConfig(company);

  const scenarioKind = kind === 'PFA' ? 'pfa-scenario' : 'project-pricing-scenario';
  const projectKind = kind;
  const config = kind === 'PFA' ? PFAScenarioConfiguration : ProjectPricingScenarioConfiguration;

  // Initialize scenario and project only once
  useEffect(() => {
    if (hasInitializedRef.current) return; // Prevent multiple initializations
    
    async function loadAndRunScenario() {
        let projects = await getAmProjects(companyId, projectKind);
        let firstProjectId = projects?.[0]?.id ?? uuidv4().substring(0, 8);
        let scenarioList = await listWhatIfScenarios(companyId, scenarioKind);
        let targetScenario = scenarioList[0];
        const scenarioRecord = await getWhatIfScenario(companyId, targetScenario.scenarioId);
        
        setQATProjectId(firstProjectId);
        setScenario(scenarioRecord);
        let localScenarioStatus = scenarioRecord.status;
        setScenarioStatus(localScenarioStatus);
        const canRunScenario = localScenarioStatus && (isFinalStateOfScenario(localScenarioStatus) || localScenarioStatus === "new");
        if(canRunScenario){
            hasInitializedRef.current = true;
            runScenario(scenarioRecord, firstProjectId);
        }
    }
    setLoading(true);
    loadAndRunScenario().catch(e => errorMessage(e.message)).finally(() => setLoading(false));
  }, []);

  // Auto-scroll to results when they appear
  useEffect(() => {
    if ((resultantStore || running) && resultBlockRef.current) {
      resultBlockRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [resultantStore, running]);

  const updateScenarioWithInputs = async (scenarioRecord: WhatIfScenarioRecord) => {
    if (!scenarioRecord || !inputs) return;
    try {
      // Get the scenario inputs for the current scenario kind
      const scenarioInputs = inputs[scenarioKind];
      if (!scenarioInputs) return;

      // Use the mapping function to convert inputs to the proper format
      const mappedGlobalInputs = mapInputsToScenarioFormat(config, scenarioInputs);
      const updatedGlobalInputs = {
        ...(scenarioRecord.inputData?.globalInputs ?? {}),
        ...mappedGlobalInputs
      };
      await updateWhatIfScenario(companyId, scenarioRecord.scenarioId, {
        projectVersions: scenarioRecord.inputData.projectVersions,
        globalInputs: updatedGlobalInputs,
        parameters: undefined,
        projectVersionInputs: scenarioRecord.inputData.projectVersionInputs,
        scenarioName: scenarioRecord.scenarioName,
      });

      // Update local scenario state
      setScenario({
        ...scenarioRecord,
        inputData: {
          ...scenarioRecord.inputData,
          globalInputs: updatedGlobalInputs
        }
      });
    } catch (e: any) {
      console.error(`Error updating scenario. ${e.message}`);
      errorMessage(`Error ingesting the provided inputs`);
      throw e;
    }
  };

  const runScenario = async (scenarioRecord: WhatIfScenarioRecord, projectId: string) => {
    let QATProjectId = projectId;
    clear();
    setRunning(true);
    setResultantStore(undefined);
    setVersionData(undefined);

    try {
      await updateScenarioWithInputs(scenarioRecord);
      setScenarioStatus("started");

      const result = await runWhatIfScenarioDirectlyForQAT(companyId, scenarioRecord.scenarioId, QATProjectId, "[new]");
      
      if (result.type === "error") {
        errorMessage("Something went wrong please try again");
        setScenarioStatus("failed");
        return;
      } else if (result?.newVersion && result?.newVersion?.versionData) {
        const store = await persistence.loadLineItemsStore(
          collection, 
          getStoreId(QATProjectId, result?.newVersion?.versionData?.versionId), 
          false
        );
        setScenarioStatus("completed");
        setResultantStore(store);
        setVersionData(result?.newVersion.versionData);
        onComplete?.();
      }
    } catch (e: any) {
      errorMessage(`Error running scenario. ${e.message}`);
    } finally {
      setRunning(false);
    }
  }

  if (loading) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        background: 'rgba(0, 123, 255, 0.05)',
        borderRadius: '12px',
        border: '1px solid rgba(0, 123, 255, 0.2)'
      }}>
        <Icon name='spinner' loading size="large" style={{ color: '#007bff', marginBottom: '12px' }} />
        <p style={{ 
          margin: '8px 0 0 0', 
          fontSize: '16px', 
          color: '#007bff',
          fontWeight: '500'
        }}>
          Initializing {kind === 'PFA' ? 'Preliminary Financial Analysis' : 'Project Pricing'} scenario...
        </p>
      </div>
    );
  }

  const title = kind === 'PFA' ? 'Preliminary Financial Analysis Results' : 'Project Pricing Results';

  return (
    <div ref={resultBlockRef}>
      <PSCard>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '32px',
          paddingBottom: '20px',
          borderBottom: '1px solid #f1f3f4'
        }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '20px'
          }}>
            <Icon name='chart line' inverted size="large" />
          </div>
          <div>
            <h2 style={{ 
              margin: 0, 
              fontSize: '28px', 
              fontWeight: '600', 
              color: '#2c3e50',
              lineHeight: '1.3'
            }}>
              {title}
            </h2>
          </div>
        </div>

        {/* Results */}
        {(resultantStore && kind === 'PFA') && (
          <PFAResultBlock 
            store={resultantStore} 
            versionData={versionData} 
            QATProjectId={QATProjectId!} 
          />
        )}
        
        {(resultantStore && kind === "Project-Pricing") && (
          <ProjectPricingResultBlock 
            store={resultantStore} 
            versionData={versionData} 
            QATProjectId={QATProjectId!} 
          />
        )}

        {/* Running State */}
        {running && (
          <div>
            <h3 style={{ 
              margin: 0, 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#2c3e50',
              lineHeight: '1.3'
            }}>
              Running Analysis
            </h3>
            <p style={{ 
              margin: '4px 0 0 0', 
              fontSize: '16px', 
              color: '#6c757d',
              lineHeight: '1.4'
            }}>
              {`Your ${kind.split("-").join(" ")} scenario analysis is currently running. This process may take a few minutes to complete. Please wait while we process your financial data.`}
            </p>
            <div style={{ 
              textAlign: 'center', 
              padding: '20px',
              background: 'rgba(0, 123, 255, 0.05)',
              borderRadius: '12px',
              border: '1px solid rgba(0, 123, 255, 0.2)',
              marginTop: '16px'
            }}>
              <Icon name='spinner' loading size="large" style={{ color: '#007bff', marginBottom: '12px' }} />
              <p style={{ 
                margin: '8px 0 0 0', 
                fontSize: '16px', 
                color: '#007bff',
                fontWeight: '500'
              }}>
                Processing your scenario analysis...
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {scenarioStatus === 'failed' && (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            background: 'rgba(220, 53, 69, 0.05)',
            borderRadius: '12px',
            border: '1px solid rgba(220, 53, 69, 0.2)'
          }}>
            <Icon name='exclamation triangle' size="large" style={{ color: '#dc3545', marginBottom: '12px' }} />
            <p style={{ 
              margin: '8px 0 0 0', 
              fontSize: '16px', 
              color: '#dc3545',
              fontWeight: '500'
            }}>
              Failed to run scenario analysis. Please try again.
            </p>
          </div>
        )}
      </PSCard>
    </div>
  );
}
